'use client';

import { createContext, useContext } from 'react';

/**
 * Extra analytics context for a component tree that is mounted outside its usual page (SSD-6665).
 *
 * The Skin Analysis routine's product sheet reuses PDP sections, and those sections report as
 * PDP (`page: 'pdp'`). Wrapping the sheet in this provider lets every `trackCustom` event raised
 * inside it say where it really happened, without the PDP components knowing about the routine.
 */
export interface AnalyticsSurface {
  /**
   * Added to every `trackCustom` event raised inside the provider. The caller's own params win on a
   * key clash: the surface describes where the event happened, never what the caller measured.
   */
  readonly params: Readonly<Record<string, unknown>>;
  /**
   * Re-labels `page` only where it holds exactly `from`, so a PDP section inside the sheet stops
   * reading as the product page while an unrelated `page` (a review list's page number) is kept.
   */
  readonly pageOverride?: { readonly from: string; readonly to: string };
}

/**
 * Optional context, deliberately not `createSafeContext`: `useAnalyticsEvent` runs everywhere, and
 * almost every caller renders outside any surface. `null` is the documented default and means
 * "no surface" — events go out exactly as the caller built them.
 */
const AnalyticsSurfaceContext = createContext<AnalyticsSurface | null>(null);

export const AnalyticsSurfaceProvider = AnalyticsSurfaceContext.Provider;

export const useAnalyticsSurface = (): AnalyticsSurface | null => useContext(AnalyticsSurfaceContext);

/** The params an event is finally sent with once the surface, if any, is applied. */
export function applyAnalyticsSurface(
  surface: AnalyticsSurface | null,
  params: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (surface == null) return params;

  const { pageOverride } = surface;
  const isOverriddenPage = pageOverride != null && params?.page === pageOverride.from;
  return {
    ...surface.params,
    ...params,
    ...(isOverriddenPage ? { page: pageOverride.to } : {}),
  };
}
